import {
  AdjustmentsIcon,
  AnnotationIcon,
  BadgeCheckIcon,
  BanIcon,
  BeakerIcon,
  BriefcaseIcon,
  ChartSquareBarIcon,
  CheckCircleIcon,
  ClipboardListIcon,
  ClockIcon,
  CollectionIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  DatabaseIcon,
  DocumentReportIcon,
  DocumentTextIcon,
  DownloadIcon,
  FingerPrintIcon,
  GiftIcon,
  GlobeIcon,
  HomeIcon,
  IdentificationIcon,
  LibraryIcon,
  LockClosedIcon,
  OfficeBuildingIcon,
  PresentationChartBarIcon,
  PuzzleIcon,
  ReceiptRefundIcon,
  RefreshIcon,
  ScaleIcon,
  SearchIcon,
  SparklesIcon,
  SpeakerphoneIcon,
  TableIcon,
  TicketIcon,
  UsersIcon,
  ViewListIcon,
  XCircleIcon,
} from '@heroicons/react/outline';
import type {JSX, SVGProps} from 'react';

import {
  AfriexAdminPermission,
  AfriexFeatureCode,
} from '@afriex/core/domain/enums';

import {AfriexVerificationStatus} from '@/types';

import {appPaths} from './appPaths';

export interface ISubMenuConfig {
  label: string;
  path: string;
  /** Hidden unless the admin's role holds it; omitted when open to all staff. */
  permission?: AfriexAdminPermission;
  icon?: (props: SVGProps<SVGSVGElement>) => JSX.Element;
  /** Hidden unless this feature is switched on for anyone. */
  featureCode?: AfriexFeatureCode;
}

export interface IMenuConfig extends ISubMenuConfig {
  subMenu: ISubMenuConfig[];
}

export const menuConfig: IMenuConfig[] = [
  {
    label: 'Home',
    path: appPaths.home,
    icon: HomeIcon,
    subMenu: [],
  },
  {
    label: 'Merchants',
    path: appPaths.merchants,
    icon: BriefcaseIcon,
    subMenu: [
      {
        label: 'Invitations',
        icon: TicketIcon,
        path: `${appPaths.merchants}/invitations`,
      },
      {
        label: 'Businesses',
        icon: HomeIcon,
        path: `${appPaths.merchants}/businesses`,
      },
      {
        label: 'Users',
        icon: UsersIcon,
        path: `${appPaths.merchants}/users`,
      },
      {
        label: 'Transactions',
        icon: ChartSquareBarIcon,
        path: `${appPaths.merchants}/transactions`,
      },
      {
        label: 'Live Access',
        icon: BadgeCheckIcon,
        path: `${appPaths.merchants}/live-access`,
        permission: AfriexAdminPermission.BusinessLiveAccessRead,
      },
      {
        label: 'KYC',
        icon: FingerPrintIcon,
        path: `${appPaths.merchants}/kyc`,
      },
      {
        label: 'Invoices',
        icon: DocumentTextIcon,
        path: `${appPaths.merchants}/invoices`,
      },
      {
        label: 'Fees',
        icon: CurrencyDollarIcon,
        path: `${appPaths.merchants}/fees`,
      },
      {
        label: 'Pricing Negotiations',
        icon: AdjustmentsIcon,
        path: `${appPaths.merchants}/pricing-negotiations`,
        permission: AfriexAdminPermission.BusinessFeeNegotiationRead,
        featureCode: AfriexFeatureCode.BUSINESS_PRICING_NEGOTIATION,
      },
      {
        label: 'Rates',
        icon: RefreshIcon,
        path: `${appPaths.merchants}/rates`,
      },
      {
        label: 'Accounts',
        icon: CollectionIcon,
        path: `${appPaths.merchants}/pool-accounts`,
      },
      {
        label: 'Limits',
        icon: ScaleIcon,
        path: `${appPaths.merchants}/limits`,
      },
      {
        label: 'PIN Resets',
        icon: LockClosedIcon,
        path: `${appPaths.merchants}/pin-reset-requests`,
      },
      {
        label: 'Marketplace',
        icon: ChartSquareBarIcon,
        path: `${appPaths.merchants}/marketplace`,
        permission: AfriexAdminPermission.MarketPlaceRead,
      },
      {
        label: 'Feature Flags',
        icon: LibraryIcon,
        path: `${appPaths.merchants}/features`,
      },
      {
        label: 'Affiliate Program',
        icon: GiftIcon,
        path: appPaths.merchantAffiliate,
        permission: AfriexAdminPermission.BusinessAffiliateRead,
      },
      {
        label: 'Logs',
        icon: ClipboardListIcon,
        path: `${appPaths.merchants}/logs`,
      },
    ],
  },
  {
    label: 'Transactions',
    path: appPaths.transactions,
    icon: ChartSquareBarIcon,
    permission: AfriexAdminPermission.TransactionRead,
    subMenu: [],
  },
  {
    label: 'Subscription Plans',
    path: appPaths.subscriptionPlans,
    icon: CreditCardIcon,
    permission: AfriexAdminPermission.SubscriptionPlanRead,
    subMenu: [],
  },
  {
    label: 'Users',
    path: appPaths.users,
    icon: UsersIcon,
    subMenu: [
      {
        label: 'List',
        icon: ViewListIcon,
        path: appPaths.users,
      },
      {
        label: 'App Metrics',
        icon: ChartSquareBarIcon,
        path: `${appPaths.users}/metrics`,
      },
    ],
  },
  {
    label: 'KYC',
    path: appPaths.kyc,
    icon: FingerPrintIcon,
    subMenu: [
      {
        label: 'Pending',
        icon: ClockIcon,
        path: `${appPaths.kyc}/?status=${AfriexVerificationStatus.Pending}`,
      },
      {
        label: 'Failed',
        icon: XCircleIcon,
        path: `${appPaths.kyc}/?status=${AfriexVerificationStatus.Failed}`,
      },
      {
        label: 'Verified',
        icon: CheckCircleIcon,
        path: `${appPaths.kyc}/?status=${AfriexVerificationStatus.Success}`,
      },
      {
        label: 'Reuploaded',
        icon: CheckCircleIcon,
        path: `${appPaths.kyc}/?status=${AfriexVerificationStatus.Reuploaded}`,
      },
      {
        label: 'Requested Docs',
        icon: CheckCircleIcon,
        path: `${appPaths.kyc}/?status=${AfriexVerificationStatus.ReuploadRequested}`,
      },
    ],
  },
  {
    label: 'Referrals',
    path: appPaths.referral,
    icon: SparklesIcon,
    subMenu: [
      {
        label: 'List',
        icon: ViewListIcon,
        path: `${appPaths.referral}/list`,
      },
      {
        label: 'Tiers',
        icon: CollectionIcon,
        path: `${appPaths.referral}/tiers`,
      },
    ],
  },
  {
    label: 'Notifications',
    path: appPaths.notification,
    icon: SpeakerphoneIcon,
    subMenu: [],
    permission: AfriexAdminPermission.PushNotificationSend,
  },
  {
    label: 'Disputes',
    path: appPaths.disputes,
    icon: ReceiptRefundIcon,
    subMenu: [],
    permission: AfriexAdminPermission.DisputeRead,
  },
  {
    label: 'Rates',
    path: appPaths.rates,
    icon: RefreshIcon,
    subMenu: [
      {
        label: 'Trade Logs',
        icon: DocumentReportIcon,
        path: `${appPaths.rates}/trade-logs`,
      },
    ],
  },
  {
    label: 'Fees',
    path: appPaths.fees,
    icon: CurrencyDollarIcon,
    subMenu: [],
    permission: AfriexAdminPermission.TransactionFeeRead,
  },
  {
    label: 'Processors',
    icon: LibraryIcon,
    path: appPaths.processors,
    subMenu: [
      {
        label: 'List',
        icon: ViewListIcon,
        path: `${appPaths.processors}`,
      },
      {
        label: 'Balance',
        icon: CurrencyDollarIcon,
        path: `${appPaths.processors}/balance`,
      },
      {
        label: 'Corridors',
        icon: CurrencyDollarIcon,
        path: `${appPaths.processors}/corridors`,
      },
      {
        label: 'Resolvers',
        icon: SearchIcon,
        path: `${appPaths.processors}/resolvers`,
      },
      {
        label: 'Banks',
        icon: OfficeBuildingIcon,
        path: `${appPaths.processors}/banks`,
      },
      {
        label: 'Webhook Simulator',
        icon: SparklesIcon,
        path: `${appPaths.processors}/webhooks-simulator`,
      },
      {
        label: 'Transactions',
        icon: TableIcon,
        path: `${appPaths.processors}/transactions`,
      },
      {
        label: 'Metrics',
        icon: PresentationChartBarIcon,
        path: `${appPaths.processors}/metrics`,
      },
      {
        label: 'Test Transactions',
        icon: BeakerIcon,
        path: `${appPaths.processors}/test-transactions`,
      },
    ],
  },
  {
    label: 'Blocked',
    icon: BanIcon,
    path: appPaths.blockedPaymentMethods,
    subMenu: [
      {
        label: 'Methods',
        icon: PuzzleIcon,
        path: appPaths.blockedPaymentMethods,
        permission: AfriexAdminPermission.BlockedPaymentMethodRead,
      },
    ],
    permission: AfriexAdminPermission.BlockedPaymentMethodRead,
  },
  {
    label: 'Rewards',
    path: appPaths.rewards,
    icon: GiftIcon,
    subMenu: [
      {
        label: 'List',
        icon: ChartSquareBarIcon,
        path: appPaths.cards,
        permission: AfriexAdminPermission.RewardRead,
      },
    ],
    permission: AfriexAdminPermission.RewardRead,
  },
  {
    label: 'Product Updates',
    path: appPaths.productUpdates,
    icon: AnnotationIcon,
    subMenu: [],
    permission: AfriexAdminPermission.BusinessUpdateRead,
  },
  {
    label: 'Audit Logs',
    icon: ClipboardListIcon,
    path: appPaths.parameterStore,
    subMenu: [
      {
        label: 'Parameter Store',
        icon: DatabaseIcon,
        path: appPaths.parameterStore,
      },
    ],
  },
  {
    label: 'Settings',
    icon: AdjustmentsIcon,
    path: appPaths.settings,
    subMenu: [
      {
        label: 'Team',
        icon: IdentificationIcon,
        path: appPaths.admins,
      },
      {
        label: 'Countries & Currencies',
        icon: GlobeIcon,
        path: appPaths.config,
      },
      {
        label: 'App Releases',
        icon: DownloadIcon,
        path: appPaths.appReleases,
      },
    ],
  },
];

export interface DashCardConfig {
  label: string;
  valueType: 'money' | 'rate';
}

export const dashCardConfig: DashCardConfig[] = [
  {
    label: 'Transaction Count',
    valueType: 'rate',
  },
  {
    label: 'Debits',
    valueType: 'rate',
  },
  {
    label: 'Debit Value',
    valueType: 'money',
  },
  {
    label: 'Credits',
    valueType: 'rate',
  },
  {
    label: 'Credit Value',
    valueType: 'money',
  },
];
